import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';

import {
  CycleStage,
  SubmissionStatus,
  type GroupMemberResponse,
  type GroupStandingResponse,
  type GroupSubmissionResponse,
} from '../../api';
import {
  AsyncSection,
  DataTable,
  FormError,
  PageHeader,
  Panel,
  useNotifications,
  type Column,
} from '../../components';
import { formatDateTime, formatInteger } from '../../format';
import {
  PATHS,
  staffSubmissionConsolidatedPath,
  staffSubmissionContractPath,
} from '../../routes/paths';
import { saveAsAttachment } from '../../utils/download';
import {
  useGroupFileDownload,
  useGroupFileStanding,
  useMarkGroupFileFailed,
  useMarkGroupFileSent,
  usePrepareGroupFile,
} from '../queries';
import {
  CHANNEL_LABELS,
  STAGE_LABELS,
  SUBMISSION_COPY,
  SUBMISSION_STATUS_LABELS,
  approvedOf,
  byCompany,
  contractLabel,
  currentCycleMonth,
} from './submissionCycle';
import { MarkFailedForm, MarkSentForm } from './SubmissionMarkerForms';

/**
 * One economic group's month with one insurer that takes a single file for it.
 *
 * The company file page one level up. When an insurer's layout gathers a
 * customer's contracts into one workbook and the customer's companies form a
 * group, the workbook is the group's: every contract of every company in it.
 * Each member is still approved on its own, in the review queue; this page is
 * where the month is packaged, filed and marked, once, for all of them -- and
 * the contract and company pages send the analyst here.
 *
 * The address carries the group and the insurer as opaque uuids and the month
 * as `?ciclo=YYYY-MM`, for the reasons the contract page gives.
 */
export function SubmissionGroupPage() {
  const { grupoId = '', operadoraId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const cycleMonth = searchParams.get('ciclo') ?? currentCycleMonth();

  const standing = useGroupFileStanding(grupoId, operadoraId, cycleMonth);

  return (
    <div className="page">
      <AsyncSection
        state={standing}
        loadingLabel="Carregando o fechamento do grupo…"
        errorTitle="Não foi possível carregar o fechamento do grupo"
      >
        {(data) => (
          <>
            <PageHeader
              title={SUBMISSION_COPY.groupTitle}
              description={`${SUBMISSION_COPY.groupLabel} ${data.group_name} · ${data.insurer_name} — ${data.cycle_month}`}
              actions={
                <Link className="button button--ghost" to={PATHS.staffSubmissions}>
                  {SUBMISSION_COPY.backToCalendar}
                </Link>
              }
            />
            <GroupPanel standing={data} />
            <Panel
              title={SUBMISSION_COPY.groupMembersTitle}
              description={SUBMISSION_COPY.groupMembersDescription}
            >
              {byCompany(data.members).map(([companyName, members]) => (
                <DataTable
                  id="group-file-contracts"
                  key={members[0]?.company_id ?? companyName}
                  caption={companyName}
                  columns={memberColumns(data.cycle_month)}
                  rows={members}
                  rowKey={(row) => row.contract_id}
                  emptyTitle="Nenhum contrato"
                />
              ))}
            </Panel>
            <Panel
              title={SUBMISSION_COPY.attemptsTitle}
              description={SUBMISSION_COPY.groupAttemptsDescription}
            >
              <DataTable
                id="group-files"
                caption="Arquivos do grupo deste mês"
                columns={ATTEMPT_COLUMNS}
                rows={data.attempts}
                rowKey={(row) => row.id}
                emptyTitle={SUBMISSION_COPY.noAttempts}
              />
            </Panel>
          </>
        )}
      </AsyncSection>
    </div>
  );
}

/** The month and the one thing to do about it next, as the company panel has it. */
function GroupPanel({ standing }: { standing: GroupStandingResponse }) {
  const { notify } = useNotifications();
  const prepare = usePrepareGroupFile();
  const markSent = useMarkGroupFileSent();
  const markFailed = useMarkGroupFileFailed();
  const [marking, setMarking] = useState<'sent' | 'failed' | null>(null);

  const file = standing.submission ?? null;
  const missing = standing.members.filter((member) => !member.approved);
  const held = standing.members.filter((member) =>
    standing.held_elsewhere_contract_ids.includes(member.contract_id),
  );
  const alreadyFiled = standing.members.filter((member) =>
    standing.already_filed_contract_ids.includes(member.contract_id),
  );
  const ambiguous = standing.members.filter((member) =>
    standing.ambiguous_contract_ids.includes(member.contract_id),
  );

  return (
    <Panel
      title={SUBMISSION_COPY.groupStandingTitle}
      description={SUBMISSION_COPY.groupDescription}
    >
      <dl className="definitions">
        <div>
          <dt>Ciclo</dt>
          <dd>{standing.cycle_month}</dd>
        </div>
        <div>
          <dt>Situação</dt>
          <dd>{STAGE_LABELS[standing.stage]}</dd>
        </div>
        <div>
          <dt>Empresas</dt>
          <dd>{formatInteger(standing.company_count)}</dd>
        </div>
        <div>
          <dt>Contratos aprovados</dt>
          <dd>{approvedOf(standing.approved_count, standing.contract_count)}</dd>
        </div>
      </dl>

      {missing.length > 0 && (
        <div className="panel__footnote panel__footnote--warning" role="status">
          <p>{SUBMISSION_COPY.groupMissingApprovals}</p>
          <ul>
            {missing.map((member) => (
              <li key={member.contract_id}>{memberLabel(member)}</li>
            ))}
          </ul>
        </div>
      )}

      {ambiguous.length > 0 && (
        <div className="panel__footnote panel__footnote--warning" role="status">
          <p>{SUBMISSION_COPY.groupAmbiguous}</p>
          <ul>
            {ambiguous.map((member) => (
              <li key={member.contract_id}>{memberLabel(member)}</li>
            ))}
          </ul>
        </div>
      )}

      {held.length > 0 && (
        <div className="panel__footnote panel__footnote--warning" role="status">
          <p>{SUBMISSION_COPY.groupHeldElsewhere}</p>
          <ul>
            {held.map((member) => (
              <li key={member.contract_id}>
                <Link
                  to={
                    member.submission?.consolidated_submission_id != null
                      ? staffSubmissionConsolidatedPath(
                          member.company_id,
                          standing.insurer_id,
                          standing.cycle_month,
                        )
                      : staffSubmissionContractPath(member.contract_id, standing.cycle_month)
                  }
                >
                  {memberLabel(member)}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {alreadyFiled.length > 0 && standing.submission == null && (
        <div className="panel__footnote" role="status">
          <p>{SUBMISSION_COPY.groupAlreadyFiled}</p>
          <ul>
            {alreadyFiled.map((member) => (
              <li key={member.contract_id}>{memberLabel(member)}</li>
            ))}
          </ul>
        </div>
      )}

      {standing.can_prepare && (
        <>
          <p className="panel__footnote">{SUBMISSION_COPY.readyToPrepareGroup}</p>
          <FormError error={prepare.error} />
          <div className="form__actions">
            <button
              type="button"
              className="button button--primary"
              disabled={prepare.isPending}
              onClick={() =>
                prepare.mutate(
                  {
                    groupId: standing.group_id,
                    insurerId: standing.insurer_id,
                    cycleMonth: standing.cycle_month,
                  },
                  { onSuccess: () => notify('success', SUBMISSION_COPY.preparedGroup) },
                )
              }
            >
              {SUBMISSION_COPY.prepareGroup}
            </button>
          </div>
        </>
      )}

      {file != null && <FileEvidence file={file} />}

      {file != null && file.status === SubmissionStatus.PREPARED && (
        <>
          <p className="panel__footnote">{SUBMISSION_COPY.preparedFootnote}</p>
          {marking === null && (
            <div className="form__actions">
              <button
                type="button"
                className="button button--primary"
                onClick={() => setMarking('sent')}
              >
                {SUBMISSION_COPY.markSent}
              </button>
              <button
                type="button"
                className="button button--secondary"
                onClick={() => setMarking('failed')}
              >
                {SUBMISSION_COPY.markFailed}
              </button>
            </div>
          )}
          {marking === 'sent' && (
            <MarkSentForm
              allowProofDocument={false}
              pending={markSent.isPending}
              error={markSent.error}
              onCancel={() => setMarking(null)}
              onSubmit={({ channel, reference, notes, sentAt }) =>
                markSent.mutate(
                  { groupFileId: file.id, channel, reference, notes, sentAt },
                  {
                    onSuccess: () => {
                      notify('success', SUBMISSION_COPY.sent);
                      setMarking(null);
                    },
                  },
                )
              }
            />
          )}
          {marking === 'failed' && (
            <MarkFailedForm
              pending={markFailed.isPending}
              error={markFailed.error}
              onCancel={() => setMarking(null)}
              onSubmit={(reason) =>
                markFailed.mutate(
                  { groupFileId: file.id, reason },
                  {
                    onSuccess: () => {
                      notify('warning', SUBMISSION_COPY.failed);
                      setMarking(null);
                    },
                  },
                )
              }
            />
          )}
        </>
      )}

      {standing.stage === CycleStage.SENT && (
        <p className="panel__footnote">{SUBMISSION_COPY.sentFootnote}</p>
      )}

      <p className="panel__footnote">{SUBMISSION_COPY.noTransmission}</p>
    </Panel>
  );
}

/** The file standing for the month: its digest, the download and who did what. */
function FileEvidence({ file }: { file: GroupSubmissionResponse }) {
  const download = useGroupFileDownload();
  const { notify } = useNotifications();

  return (
    <dl className="definitions">
      <div>
        <dt>Arquivo</dt>
        <dd>
          {file.file_name}
          <br />
          <code>{file.file_sha256}</code>
        </dd>
      </div>
      <div>
        <dt>Linhas</dt>
        <dd>{formatInteger(file.row_count)}</dd>
      </div>
      <div className="form__actions">
        <button
          type="button"
          className="button button--secondary"
          disabled={download.isPending}
          onClick={() =>
            download.mutate(
              { groupFileId: file.id },
              {
                onSuccess: ({ blob, filename }) =>
                  saveAsAttachment(blob, filename ?? file.file_name),
                onError: () => notify('error', SUBMISSION_COPY.downloadFailed),
              },
            )
          }
        >
          {download.isPending ? SUBMISSION_COPY.downloading : SUBMISSION_COPY.downloadFile}
        </button>
      </div>
      <div>
        <dt>Gerado por</dt>
        <dd>
          {file.prepared_by.display_name} em {formatDateTime(file.prepared_at)}
        </dd>
      </div>
      {file.status === SubmissionStatus.SENT && (
        <>
          <div>
            <dt>Enviado por</dt>
            <dd>
              {file.sent_by?.display_name ?? '—'} em {formatDateTime(file.submitted_at)}
            </dd>
          </div>
          <div>
            <dt>Por onde</dt>
            <dd>{file.channel != null ? CHANNEL_LABELS[file.channel] : '—'}</dd>
          </div>
          <div>
            <dt>Comprovação</dt>
            <dd>{proofOfFile(file)}</dd>
          </div>
        </>
      )}
    </dl>
  );
}

function memberLabel(member: GroupMemberResponse): string {
  return `${member.company_name}: ${contractLabel(member)}`;
}

function memberColumns(cycleMonth: string): readonly Column<GroupMemberResponse>[] {
  return [
    {
      key: 'contract',
      header: 'Contrato',
      render: (row) => (
        <Link to={staffSubmissionContractPath(row.contract_id, cycleMonth)}>
          {contractLabel(row)}
        </Link>
      ),
    },
    {
      key: 'approval',
      header: 'Aprovação',
      render: (row) =>
        row.approved ? (
          <span className="badge badge--ok">Aprovado</span>
        ) : (
          <span className="badge badge--warning">Sem aprovação</span>
        ),
    },
    { key: 'stage', header: 'Situação', render: (row) => STAGE_LABELS[row.stage] },
    {
      key: 'rows',
      header: 'Linhas',
      align: 'end',
      render: (row) =>
        row.row_count != null ? (
          formatInteger(row.row_count)
        ) : (
          <span className="table__muted">—</span>
        ),
    },
  ];
}

/** Every file of the month, refused ones included, newest first. */
const ATTEMPT_COLUMNS: readonly Column<GroupSubmissionResponse>[] = [
  { key: 'status', header: 'Situação', render: (row) => SUBMISSION_STATUS_LABELS[row.status] },
  { key: 'prepared', header: 'Gerado em', render: (row) => formatDateTime(row.prepared_at) },
  {
    key: 'members',
    header: 'Empresas / contratos',
    align: 'end',
    render: (row) => `${formatInteger(row.company_count)} / ${formatInteger(row.contract_count)}`,
  },
  { key: 'rows', header: 'Linhas', align: 'end', render: (row) => formatInteger(row.row_count) },
  {
    key: 'outcome',
    header: 'Comprovação',
    render: (row) =>
      row.failure_reason != null ? (
        <span className="table__muted">{row.failure_reason}</span>
      ) : row.status === SubmissionStatus.SENT ? (
        proofOfFile(row)
      ) : (
        <span className="table__muted">—</span>
      ),
  },
];

function proofOfFile(file: GroupSubmissionResponse): string {
  const parts: string[] = [];
  if (file.reference != null && file.reference !== '') {
    parts.push(`Protocolo ${file.reference}`);
  }
  if (file.notes != null && file.notes !== '') {
    parts.push(file.notes);
  }
  return parts.length > 0 ? parts.join(' · ') : 'Nada registrado';
}
